import React, { useEffect, useMemo, useState } from 'react';
import './css/index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useInputState from '../../helpers/hooks/useInputState';
import PageTitle from '../../components/PageTitle';
import MyButton from '../../components/MyButton';
import SearchInput from '../../components/SearchInput';
import SelectButton from '../../components/SelectButton';
import SelectCategory from './components/SelectCategory';
import {
  getCategoriesRequest,
  getLanguagesRequest,
} from '../../store/actions/staticData';
import SelectSubCategories from './components/SelectSubCategories';
import SelectCity from './components/SelectCity';
import MapView from './components/MapView';
import DaysSection, { days } from './components/DaysSection';
import ContactsSection from './components/ContactsSection';
import getNameToShowOfMany from '../../helpers/getNameToShowOfMany';
import SelectLanguages from './components/SelectLanguages';
import SelectTags from './components/SelectTags';
import { WORDS_LIST } from '../../helpers/getWordForm';
import SelectPhotos from './components/SelectPhotos';
import checkAddOrganizationPayload from '../../helpers/checkAddOrganizationPayload';
import MyModal from '../../components/MyModal';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import SelectAdditionalCities from './components/SelectAdditionalCities';
import SelectTemplatePhotos from './components/SelectTemplatePhotos';
import {
  buildAutofillReferenceFileContent,
  fetchAllStaticDataForAutofill,
  parseOrganizationAutofillJson,
} from '../../helpers/organizationAutofill';

const defaultSchedule = days.reduce((acc, day) => ({ ...acc, [day.value]: { start: '', end: '' } }), {});
const tempComponentStates = {
  categories: 'categories',
  subCategories: 'subCategories',
  city: 'city',
  additionalCities: 'additionalCities',
  languages: 'languages',
  tags: 'tags',
  photos: 'photos',
  templatePhotos: 'templatePhotos',
};

function AddOrganization() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categories: categoriesFromStore, languages: languagesFromStore } = useSelector((store) => store.staticData);
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('id');
  const isEditing = Boolean(organizationId);

  const [gettingLoading, setGettingLoading] = useState(isEditing);
  const [gettingError, setGettingError] = useState('');

  const [ownerId, setOwnerId] = useInputState();
  const [name, setName] = useInputState();
  const [category, setCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [specialization, setSpecialization] = useInputState();
  const [city, setCity] = useState(null);
  const [additionalCities, setAdditionalCities] = useState([]);
  const [address, setAddress] = useInputState();
  const [coordinates, setCoordinates] = useState(null);
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [contacts, setContacts] = useState({});
  const [description, setDescription] = useInputState();
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [templatePhotoIds, setTemplatePhotoIds] = useState([]);

  const [tempComponentState, setTempComponentState] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalState, setModalState] = useState('');
  const [loading, setLoading] = useState(false);

  const [dropdownAddresses, setDropdownAddresses] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [isAutofillModalOpen, setIsAutofillModalOpen] = useState(false);
  const [autofillJsonText, setAutofillJsonText] = useState('');
  const [autofillError, setAutofillError] = useState('');
  const [autofillDownloadLoading, setAutofillDownloadLoading] = useState(false);
  const [autofillConvertLoading, setAutofillConvertLoading] = useState(false);

  const subCategoriesName = useMemo(
    () => getNameToShowOfMany({ list: subCategories }),
    [subCategories],
  );
  const languagesName = useMemo(
    () => getNameToShowOfMany({ list: languages }),
    [languages],
  );
  const tagsName = useMemo(
    () => getNameToShowOfMany({
      list: tags, field: 'self', words: WORDS_LIST.tag, firstWord: '',
    }),
    [tags],
  );

  const isOnlineWorking = useMemo(() => Boolean(category?.is_online), [category]);

  const onCancelClick = () => {
    navigate('/organizations/view');
  };

  const openAutofillModal = () => {
    setAutofillError('');
    setIsAutofillModalOpen(true);
  };

  const closeAutofillModal = () => {
    setIsAutofillModalOpen(false);
    setAutofillError('');
  };

  const onDownloadAutofillFileClick = () => {
    setAutofillError('');
    setAutofillDownloadLoading(true);
    fetchAllStaticDataForAutofill()
      .then((staticPayload) => {
        const content = buildAutofillReferenceFileContent(staticPayload);
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'nomad-organization-autofill.txt';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setAutofillError('Не удалось загрузить справочники. Проверьте сеть и повторите.'))
      .finally(() => setAutofillDownloadLoading(false));
  };

  const onAutofillConvert = async () => {
    setAutofillError('');
    setAutofillConvertLoading(true);
    try {
      const staticData = await fetchAllStaticDataForAutofill();
      const result = parseOrganizationAutofillJson(autofillJsonText, staticData);
      if (result.error) {
        setAutofillError(result.error);
        return;
      }
      const { patches } = result;

      if (patches.ownerId !== undefined) setOwnerId(patches.ownerId);
      if (patches.name !== undefined) setName(patches.name);
      if (patches.specialization !== undefined) setSpecialization(patches.specialization);
      if (patches.address !== undefined) setAddress(patches.address);
      if (patches.description !== undefined) setDescription(patches.description);

      if (patches.category !== undefined) {
        setCategory(patches.category);
      }
      if (patches.subCategories !== undefined) {
        setTimeout(() => setSubCategories(patches.subCategories), patches.category !== undefined ? 100 : 0);
      }

      if (patches.city !== undefined) {
        setCity(patches.city);
      }
      if (patches.additionalCities !== undefined) {
        setAdditionalCities(patches.additionalCities);
      }

      if (patches.coordinates !== undefined) {
        setTimeout(() => setCoordinates(patches.coordinates), 300);
      }

      if (patches.schedulePartial !== undefined) {
        setSchedule((prev) => ({ ...prev, ...patches.schedulePartial }));
      }

      if (patches.contactsPartial !== undefined) {
        setContacts((prev) => ({ ...prev, ...patches.contactsPartial }));
      }

      if (patches.languages !== undefined) setLanguages(patches.languages);
      if (patches.tags !== undefined) setTags(patches.tags);
      if (patches.templatePhotoIds !== undefined) setTemplatePhotoIds(patches.templatePhotoIds);

      setIsAutofillModalOpen(false);
      setAutofillJsonText('');
    } catch (e) {
      setAutofillError('Ошибка при загрузке справочников или применении данных.');
    } finally {
      setAutofillConvertLoading(false);
    }
  };

  const onSave = async () => {
    const convertedSchedule = Object.keys(schedule)
      .reduce((acc, day) => ({
        ...acc,
        [day]: schedule[day].type === 'hours' ? `${schedule[day].start}-${schedule[day].end}` : schedule[day].type,
      }), {});

    const { errors, formData, payload } = checkAddOrganizationPayload({
      owner_id: ownerId,
      name,
      category_id: category?._id,
      sub_category_ids: subCategories.map((i) => i._id),
      must_set_subcategories: !!category?.sub_categories?.length,
      country_id: city?.country_id,
      city_id: city?._id,
      additionalCities,
      specialization,
      address,
      coordinates,
      workTime: convertedSchedule,
      contacts,
      description,
      languages,
      tags,
      photos,
      templatePhotoIds,
      withPhotos: !isEditing,
      isOnlineWorking,
    });

    if (errors?.length) {
      setModalText(`В следующих полях есть ошибки:\n\n${errors.join('\n\n')}`);
      setIsModalVisible(true);
      setModalState('error');
      return;
    }

    if (!isEditing) {
      const data = await sendRequest({
        request: Api.createOrganization,
        payload: formData,
        warnErrorText: 'while creating organization',
        setLoading,
      });

      setIsModalVisible(true);
      if (data?.status !== 'ok') {
        const errorText = data?.message || 'Не удалось установить соединение с сервером.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      } else {
        setModalText(`Организация "${name}" успешно была создана!`);
        setModalState('success');
      }
    } else {
      const data = await sendRequest({
        request: Api.editOrganization,
        payload: { payload, id: organizationId },
        warnErrorText: 'while editing organization',
        setLoading,
      });

      setIsModalVisible(true);
      if (data?.status !== 'ok') {
        const errorText = data?.message || 'Не удалось установить соединение с сервером.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      } else {
        setModalText(`Организация "${name}" успешно была редактирована!`);
        setModalState('success');
      }
    }
  };

  const onCloseModal = () => {
    if (modalState === 'error') {
      setIsModalVisible(false);
      setTimeout(() => {
        setModalText('');
        setModalState('');
      }, 400);
    } else if (modalState === 'success') {
      setIsModalVisible(false);
      navigate('/organizations/view');
    }
  };

  const getCategoriesAndSubCategoriesAtEditing = (selectedOrganization) => {
    let newCategory = {};
    const newSubCategories = [];

    if (selectedOrganization.category) {
      newCategory = categoriesFromStore.find(
        (i) => i._id === selectedOrganization.category._id,
      );

      if (
        newCategory.sub_categories
          && selectedOrganization.sub_categories.length
      ) {
        selectedOrganization.sub_categories.forEach((item) => {
          const tmp = newCategory.sub_categories.find((i) => i._id === item._id);
          if (tmp) {
            newSubCategories.push(tmp);
          }
        });
      }
    }
    return [newCategory, newSubCategories];
  };

  const getLanguagesAtEditing = (selectedOrganization) => {
    const newLanguages = [];

    if (selectedOrganization.languages?.length) {
      selectedOrganization.languages.forEach((item) => {
        const tmp = languagesFromStore.find((i) => i._id === item._id);
        if (tmp) {
          newLanguages.push(tmp);
        }
      });
    }
    return newLanguages;
  };

  const getSchedule = (selectedOrganization) => {
    const newSchedule = {};
    delete selectedOrganization.work_time.__v;
    delete selectedOrganization.work_time._id;

    Object.keys(selectedOrganization.work_time).forEach((key) => {
      const thisDayType = selectedOrganization.work_time[key];
      if (thisDayType.length === 11 && thisDayType.includes('-')) {
        // when type === hours
        const [start, end] = thisDayType.split('-');
        newSchedule[key] = { start, end, type: 'hours' };
      } else {
        newSchedule[key] = { start: '', end: '', type: thisDayType };
      }
    });

    return newSchedule;
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getOrganization,
      payload: { id: organizationId },
      warnErrorText: 'while getting single organization',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    const selectedOrganization = data.organization;
    setOwnerId(selectedOrganization.user._id);
    setName(selectedOrganization.name);
    setSpecialization(selectedOrganization.specialization);
    setAddress(selectedOrganization.address);
    setDescription(selectedOrganization.description || '');

    delete selectedOrganization.contacts.__v;
    delete selectedOrganization.contacts._id;
    setContacts(selectedOrganization.contacts);

    const [newCategory, newSubCategories] = getCategoriesAndSubCategoriesAtEditing(selectedOrganization);
    setCategory(newCategory);
    setTimeout(() => {
      setSubCategories(newSubCategories);
    }, 100);
    setCity(selectedOrganization.city);
    setAdditionalCities(selectedOrganization.additional_cities);
    setTags(selectedOrganization.tags);
    setTimeout(() => {
      setCoordinates(selectedOrganization.coordinates);
    }, 300);

    const newLanguages = getLanguagesAtEditing(selectedOrganization);
    setLanguages(newLanguages);

    const newSchedule = getSchedule(selectedOrganization);
    setSchedule(newSchedule);
  };

  const onAddressPress = async (item) => {
    setAddress(item.address_main);
    const data = await sendRequest({
      request: Api.getAddressCoordinates,
      payload: { params: { place_id: item.place_id } },
    });

    if (data?.coordinates) {
      setCoordinates(data.coordinates);
    }
  };

  const getDropdownAddresses = async ({ signal, addressVal, cityId }) => {
    const data = await sendRequest({
      request: Api.getDropdownAddresses,
      payload: { params: { address: addressVal, city_id: cityId }, signal },
      shouldLogError: false,
      setLoading: setDropdownLoading,
    });

    if (data?.status !== 'ok') {
      return setDropdownAddresses([]);
    }

    setDropdownAddresses(data.addresses);
  };

  const onMapPress = async (cords) => {
    const data = await sendRequest({
      request: Api.getPlaceNameWithCoordinates,
      payload: { params: cords },
    });

    if (data?.place_name) {
      setAddress(data.place_name);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (!address?.length || !city?._id) {
      setDropdownAddresses([]);
    } else {
      getDropdownAddresses({
        signal,
        addressVal: address,
        cityId: city._id,
      }).then();
    }

    return () => controller.abort({ response: { data: 'Aborted' } });
  }, [address, city]);

  useEffect(() => {
    setSubCategories([]);
  }, [category]);

  useEffect(() => {
    dispatch(getCategoriesRequest());
    dispatch(getLanguagesRequest());
  }, []);

  useEffect(() => {
    if (isEditing && categoriesFromStore?.length && languagesFromStore?.length) {
      initData().then();
    }
  }, [categoriesFromStore, languagesFromStore]);

  return (
    <div className="add_organization">
      <PageTitle
        text={isEditing ? 'Редактировать организацию' : 'Добавить организацию'}
        RightComponent={(
          <div className="add_organization_button_group">
            <MyButton
              text="Отменить"
              onClick={onCancelClick}
              className="add_organization_button_group_cancel"
              secondary
            />
            {!gettingError && (
            <MyButton
              text="Сохранить"
              onClick={onSave}
              className="add_organization_button_group_save"
              loading={loading}
            />
            )}
          </div>
        )}
      />
      {!isEditing && !gettingError && (
        <MyButton
          text="Автозаполнение"
          onClick={openAutofillModal}
          className="add_organization_autofill_fab"
        />
      )}
      {gettingLoading
        ? <p className="add_organization_info_text">Загрузка</p>
        : gettingError
          ? <p className="add_organization_info_text">{gettingError}</p>
          : (
            <div className="add_organization_container">
              <SearchInput
                wrapperClassName="add_organization_container_form_input"
                placeholder="ID владельца"
                withLeftIcon={false}
                value={ownerId}
                onChange={setOwnerId}
                shouldValueConsiderAsFocus={false}
              />

              <SearchInput
                wrapperClassName="add_organization_container_form_input"
                placeholder="Название"
                withLeftIcon={false}
                value={name}
                onChange={setName}
                shouldValueConsiderAsFocus={false}
              />
              <SelectButton
                label="Категория"
                value={category?.name}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.categories}
                isActive={tempComponentState === tempComponentStates.categories}
              />
              {!!category?.sub_categories?.length && (
              <SelectButton
                label="Подкатегории"
                value={subCategoriesName}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.subCategories}
                isActive={tempComponentState === tempComponentStates.subCategories}
              />
              )}
              <SearchInput
                wrapperClassName="add_organization_container_form_input"
                placeholder="Специализация"
                withLeftIcon={false}
                value={specialization}
                onChange={setSpecialization}
                shouldValueConsiderAsFocus={false}
              />
              {!isOnlineWorking && (
                <>
                  <SelectButton
                    label="Город"
                    value={city?.name}
                    setTempComponentState={setTempComponentState}
                    componentStateToSet={tempComponentStates.city}
                    isActive={tempComponentState === tempComponentStates.city}
                  />
                  {city?._id && (
                    <SearchInput
                      wrapperClassName="add_organization_container_form_input"
                      placeholder="Адрес"
                      withLeftIcon={false}
                      value={address}
                      onChange={setAddress}
                      shouldValueConsiderAsFocus={false}
                      withDropdown
                      dropdownItems={dropdownAddresses}
                      dropdownLabelField="address_full"
                      dropdownKeyField="place_id"
                      onDropdownItemPress={onAddressPress}
                      dropdownLoading={dropdownLoading}
                    />
                  )}
                  <MapView
                    city={city}
                    coordinates={coordinates}
                    setCoordinates={setCoordinates}
                    onMapPress={onMapPress}
                  />
                  {city?._id ? (
                    <SelectButton
                      label="Дополнительные города (необязательно)"
                      value={additionalCities.length ? `Выбрано ${additionalCities.length}` : ''}
                      setTempComponentState={setTempComponentState}
                      componentStateToSet={tempComponentStates.additionalCities}
                      isActive={tempComponentState === tempComponentStates.additionalCities}
                    />
                  ) : null}
                </>
              )}
              <DaysSection
                setSchedule={setSchedule}
                schedule={schedule}
              />
              <ContactsSection
                contacts={contacts}
                setContacts={setContacts}
              />
              <SearchInput
                wrapperClassName="add_organization_container_descr"
                withLeftIcon={false}
                placeholder="Описание"
                value={description}
                onChange={setDescription}
                isLarge
                shouldValueConsiderAsFocus={false}
              />
              <SelectButton
                label="Языки"
                value={languagesName}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.languages}
                isActive={tempComponentState === tempComponentStates.languages}
              />
              <SelectButton
                label="Тэги"
                value={tagsName}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.tags}
                isActive={tempComponentState === tempComponentStates.tags}
              />
              {!isEditing && (
                <SelectButton
                  label="Добавить фото"
                  value={photos.length ? `${photos.length} фото` : ''}
                  setTempComponentState={setTempComponentState}
                  componentStateToSet={tempComponentStates.photos}
                  isActive={tempComponentState === tempComponentStates.photos}
                />
              )}
              <SelectButton
                label="Добавить шаблонные фото"
                value={templatePhotoIds.length ? `${templatePhotoIds.length} фото` : ''}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.templatePhotos}
                isActive={tempComponentState === tempComponentStates.templatePhotos}
              />
            </div>
          )}

      {tempComponentState && (
        <div className="add_organization_temp_container">
          {tempComponentState === tempComponentStates.categories ? (
            <SelectCategory
              setValue={setCategory}
              setTempComponentState={setTempComponentState}
            />
          ) : tempComponentState === tempComponentStates.subCategories ? (
            <SelectSubCategories
              setValue={setSubCategories}
              setTempComponentState={setTempComponentState}
              selectedCategory={category}
              initValue={subCategories}
            />
          ) : tempComponentState === tempComponentStates.city ? (
            <SelectCity
              setValue={setCity}
              setTempComponentState={setTempComponentState}
            />
          ) : tempComponentState === tempComponentStates.additionalCities ? (
            <SelectAdditionalCities
              setValue={setAdditionalCities}
              setTempComponentState={setTempComponentState}
              countryId={city?.country_id}
              initValue={additionalCities}
            />
          ) : tempComponentState === tempComponentStates.languages ? (
            <SelectLanguages
              setValue={setLanguages}
              setTempComponentState={setTempComponentState}
              initValue={languages}
            />
          ) : tempComponentState === tempComponentStates.tags ? (
            <SelectTags
              setValue={setTags}
              setTempComponentState={setTempComponentState}
              initValue={tags}
            />
          ) : tempComponentState === tempComponentStates.photos ? (
            <SelectPhotos
              setValue={setPhotos}
              setTempComponentState={setTempComponentState}
              initValue={photos}
            />
          ) : tempComponentState === tempComponentStates.templatePhotos ? (
            <SelectTemplatePhotos
              setValue={setTemplatePhotoIds}
              setTempComponentState={setTempComponentState}
              initValue={templatePhotoIds}
            />
          ) : null}
        </div>
      )}

      <MyModal isOpen={isModalVisible} closeFunction={onCloseModal}>
        <div className="add_organization_modal">
          <pre>{modalText}</pre>

          <MyButton
            className="add_organization_modal_button"
            text="Ок"
            onClick={onCloseModal}
          />
        </div>
      </MyModal>

      <MyModal isOpen={isAutofillModalOpen} closeFunction={closeAutofillModal} style={{ width: 'min(560px, 92vw)' }}>
        <div className="add_organization_autofill_modal">
          <p className="add_organization_autofill_modal_title">Автозаполнение из JSON</p>
          <p className="add_organization_autofill_modal_text">
            Скачайте файл со справочниками и инструкцией для языковой модели: сначала отправьте его в чат первым сообщением;
            после фразы «Задача ясна, отправь текст для преобразования» пришлите сухой текст, затем скопируйте JSON ответа сюда.
            Нажмите «Преобразовать» — поля формы заполнятся из объекта.
          </p>
          <p className="add_organization_autofill_modal_hint">
            Если JSON от модели не принимается и показывается ошибка разбора, вставьте его на
            {' '}
            <a
              href="https://jsonviewer.stack.hu/"
              target="_blank"
              rel="noopener noreferrer"
              className="add_organization_autofill_modal_link"
            >
              jsonviewer.stack.hu
            </a>
            {' '}
            — сервис подсветит синтаксис и поможет «выровнять» объект; скопируйте исправленный текст обратно в поле ниже.
          </p>
          <MyButton
            text="Скачать файл"
            onClick={onDownloadAutofillFileClick}
            className="add_organization_autofill_modal_download"
            loading={autofillDownloadLoading}
          />
          <textarea
            className="add_organization_autofill_modal_textarea"
            placeholder="Вставьте JSON сюда…"
            value={autofillJsonText}
            onChange={(e) => setAutofillJsonText(e.target.value)}
            rows={14}
            spellCheck={false}
          />
          {autofillError ? (
            <p className="add_organization_autofill_modal_error">{autofillError}</p>
          ) : null}
          <div className="add_organization_autofill_modal_actions">
            <MyButton
              text="Отмена"
              onClick={closeAutofillModal}
              secondary
              className="add_organization_autofill_modal_cancel"
            />
            <MyButton
              text="Преобразовать"
              onClick={onAutofillConvert}
              className="add_organization_autofill_modal_submit"
              loading={autofillConvertLoading}
            />
          </div>
        </div>
      </MyModal>
    </div>

  );
}

export default AddOrganization;
